import { compactCount, formatFaps } from "@gooner/shared";
import { CROWN_GOLD, CrownMark } from "./Crown";

/**
 * An amount of Faps, as a person sees it: THE CROWN AND THE NUMBER. Never the word.
 *
 * Two orders, on purpose (owner's rule, 2026-09-08):
 * - An AMOUNT — a balance, a price — is icon then value: "👑 661", "Steal for 👑 200". The
 *   crown here is the unit, and a unit leads. (It had drifted to value-then-icon; the
 *   bar's pill always had it right.)
 * - A SIGNED DELTA — what one act paid, a ledger row, the chest's reveal — is value then
 *   icon: "+1 👑", "+58 👑", "−200 👑". The crown there is not a unit but the fap itself,
 *   the way "+1 ❤" reads, and the sign has to lead the number.
 *
 * The crown beside a number is what a Faps amount looks like everywhere on the site — the
 * bar, the wall's badges, the buy button, the chest's reveal — and this is the one
 * component that spells it, so five surfaces cannot spell it five ways (fapsLabel, the
 * TEXT form, is for places that cannot hold an image: Discord, server sentences,
 * aria-labels, titles).
 *
 * `size` is the crown's width in px; the number inherits the surrounding font. `gold`
 * paints the number in the crown's gold — the default, because an amount is a thing
 * of value; pass false where the surrounding text already carries the colour.
 */
export function FapsAmount({
  n,
  size = 12,
  gold = true,
  signed = false,
  tone = "gold",
  compact = false,
  className,
}: {
  n: number;
  size?: number;
  gold?: boolean;
  /** "+58" / "−200" rather than "58" / "200" — ledger rows, the chest's reveal. */
  signed?: boolean;
  /** The crown's tone — `ink` on a gold button, where a gold crown vanishes. */
  tone?: "gold" | "muted" | "white" | "ink";
  /** 3.9K rather than 3,922: for a badge on a picture. Never where the exact figure matters. */
  compact?: boolean;
  className?: string;
}) {
  const whole = Math.round(n);
  const fmt = compact ? compactCount : formatFaps;
  const text = signed ? (whole < 0 ? `−${fmt(-whole)}` : `+${fmt(whole)}`) : fmt(whole);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap tabular-nums ${className ?? ""}`}
      style={gold ? { color: CROWN_GOLD } : undefined}
    >
      {signed ? (
        <>
          {text}
          <CrownMark size={size} tone={tone} />
        </>
      ) : (
        <>
          <CrownMark size={size} tone={tone} />
          {text}
        </>
      )}
    </span>
  );
}
